// Operaciones del CRM sobre Twenty, traducidas al modelo de la Consola.

import { formatPhone, phoneFromField, phoneToField, searchKey, type Phones } from '../lib/phone-number';
import { fromMicros, toMicros } from '../lib/format';
import { gqlTodos } from './gql';
import { ApiError, metadataSesion, request } from './http';
import { create, f, get, list, listAll, remove, update } from './rest';
import type {
  Correo, Empresa, Etapa, Licencia, Llamada, Miembro, Nota, Oportunidad, Persona, Tarea, Tarifa, Tipificacion, ContactoLookup,
} from './types';

/* ---------------- mapeos ---------------- */

type RawPerson = {
  id: string;
  name?: { firstName?: string; lastName?: string };
  jobTitle?: string;
  emails?: { primaryEmail?: string };
  phones?: Phones;
  companyId?: string | null;
  company?: { id: string; name: string } | null;
  noCampanas?: boolean | null;
  createdAt: string;
};

export function toPersona(r: RawPerson): Persona {
  const nombre = r.name?.firstName?.trim() ?? '';
  const apellido = r.name?.lastName?.trim() ?? '';
  return {
    id: r.id,
    nombre,
    apellido,
    nombreCompleto: `${nombre} ${apellido}`.trim() || 'Sin nombre',
    cargo: r.jobTitle ?? '',
    email: r.emails?.primaryEmail ?? '',
    telefono: phoneFromField(r.phones),
    telefonoCodigo: r.phones?.primaryPhoneCallingCode ?? '',
    empresaId: r.companyId ?? null,
    empresa: r.company ? { id: r.company.id, nombre: r.company.name } : null,
    noCampanas: !!r.noCampanas,
    creado: r.createdAt,
  };
}

type RawCompany = {
  id: string;
  name: string;
  domainName?: { primaryLinkUrl?: string };
  address?: { addressCity?: string; addressStreet1?: string; addressState?: string };
  accountOwnerId?: string | null;
  createdAt: string;
};

export function toEmpresa(r: RawCompany): Empresa {
  return {
    id: r.id,
    nombre: r.name || 'Sin nombre',
    dominio: r.domainName?.primaryLinkUrl ?? '',
    ciudad: r.address?.addressCity ?? '',
    direccion: [r.address?.addressStreet1, r.address?.addressCity, r.address?.addressState].filter(Boolean).join(', '),
    responsableId: r.accountOwnerId ?? null,
    creado: r.createdAt,
  };
}

type RawOpp = {
  id: string;
  name: string;
  amount?: { amountMicros?: number | null; currencyCode?: string | null };
  stage: string;
  closeDate?: string | null;
  companyId?: string | null;
  company?: { id: string; name: string } | null;
  pointOfContactId?: string | null;
  pointOfContact?: RawPerson | null;
  ownerId?: string | null;
  position?: number;
};

export function toOportunidad(r: RawOpp): Oportunidad {
  const contacto = r.pointOfContact ? toPersona(r.pointOfContact) : null;
  return {
    id: r.id,
    nombre: r.name || 'Sin nombre',
    monto: fromMicros(r.amount?.amountMicros),
    moneda: r.amount?.currencyCode || 'COP',
    etapa: r.stage,
    cierre: r.closeDate ?? null,
    empresaId: r.companyId ?? null,
    empresa: r.company ? { id: r.company.id, nombre: r.company.name } : null,
    contactoId: r.pointOfContactId ?? null,
    contacto: contacto ? { id: contacto.id, nombre: contacto.nombreCompleto, telefono: contacto.telefono } : null,
    responsableId: r.ownerId ?? null,
    posicion: r.position ?? 0,
  };
}

type RawLlamada = {
  id: string;
  name: string;
  direccion: Llamada['direccion'];
  estado: Llamada['estado'];
  numero: string;
  agente: string;
  inicio: string | null;
  duracionSeg: number | null;
  costo?: { amountMicros?: number | null; currencyCode?: string | null };
  destino: string;
  gestion: Llamada['gestion'];
  notas: string;
  personaId: string | null;
  empresaId: string | null;
  tipificacionId: string | null;
  persona?: RawPerson | null;
  empresa?: { id: string; name: string } | null;
  tipificacion?: { id: string; name: string } | null;
  createdAt: string;
};

export function toLlamada(r: RawLlamada): Llamada {
  const p = r.persona ? toPersona(r.persona) : null;
  return {
    id: r.id,
    nombre: r.name,
    direccion: r.direccion,
    estado: r.estado,
    numero: r.numero ?? '',
    agente: r.agente ?? '',
    inicio: r.inicio ?? r.createdAt,
    duracion: r.duracionSeg ?? 0,
    costo: fromMicros(r.costo?.amountMicros),
    moneda: r.costo?.currencyCode || 'COP',
    destino: r.destino ?? '',
    gestion: r.gestion ?? 'PENDIENTE',
    notas: r.notas ?? '',
    personaId: r.personaId,
    empresaId: r.empresaId,
    tipificacionId: r.tipificacionId,
    persona: p ? { id: p.id, nombre: p.nombreCompleto } : null,
    empresa: r.empresa ? { id: r.empresa.id, nombre: r.empresa.name } : null,
    tipificacion: r.tipificacion ? { id: r.tipificacion.id, nombre: r.tipificacion.name } : null,
    creado: r.createdAt,
  };
}

type RawTask = {
  id: string;
  title: string;
  bodyV2?: { markdown?: string | null };
  dueAt: string | null;
  status: Tarea['estado'];
  assigneeId: string | null;
  createdAt: string;
  updatedAt?: string;
  createdBy?: { name?: string } | null;
  taskTargets?: { targetPersonId?: string | null; targetCompanyId?: string | null; targetOpportunityId?: string | null; targetPerson?: RawPerson | null }[];
};

export function toTarea(r: RawTask): Tarea {
  const target = r.taskTargets?.find((t) => t.targetPersonId) ?? null;
  const persona = target?.targetPerson ? toPersona(target.targetPerson) : null;
  return {
    id: r.id,
    titulo: r.title || 'Tarea sin título',
    cuerpo: r.bodyV2?.markdown ?? '',
    vence: r.dueAt,
    estado: r.status ?? 'TODO',
    asignadoId: r.assigneeId,
    personaId: target?.targetPersonId ?? null,
    persona: persona ? { id: persona.id, nombre: persona.nombreCompleto } : null,
    empresaId: r.taskTargets?.find((t) => t.targetCompanyId)?.targetCompanyId ?? null,
    oportunidadId: r.taskTargets?.find((t) => t.targetOpportunityId)?.targetOpportunityId ?? null,
    creado: r.createdAt,
    creadoPor: r.createdBy?.name || undefined,
    actualizado: r.updatedAt,
  };
}

const memberName = (m: { name?: { firstName?: string; lastName?: string } }) =>
  `${m.name?.firstName ?? ''} ${m.name?.lastName ?? ''}`.trim();

/* ---------------- personas y empresas ---------------- */

const PERSON_ORDER = 'name.firstName[AscNullsLast]';

export async function buscarPersonas(texto: string, limit = 60, after?: string) {
  const t = texto.trim();
  const d = t.replace(/\D/g, '');
  const filter = t
    ? f.or(
        f.ilike('name.firstName', t.split(/\s+/)[0]),
        f.ilike('name.lastName', t.split(/\s+/).slice(-1)[0]),
        f.ilike('emails.primaryEmail', t),
        f.ilike('jobTitle', t),
        d.length >= 3 ? f.like('phones.primaryPhoneNumber', searchKey(d)) : null,
      )
    : undefined;
  const page = await list<RawPerson>('people', { filter, orderBy: PERSON_ORDER, limit, depth: 1, after });
  return { ...page, items: page.items.map(toPersona) };
}

export async function personasDeEmpresa(empresaId: string) {
  const items = await listAll<RawPerson>('people', { filter: f.eq('companyId', empresaId), orderBy: PERSON_ORDER, depth: 0 }, 200);
  return items.map(toPersona);
}

export const persona = async (id: string) => toPersona(await get<RawPerson>('people', id, 1));

export type PersonaInput = { nombre: string; apellido: string; cargo: string; email: string; telefono: string; empresaId: string | null };

// Solo los campos que edita el formulario. Los compuestos (correos, telefonos) se mezclan con lo que ya tiene el
// registro: Twenty reemplaza el compuesto entero, y mandar null borraria los correos y telefonos adicionales.
function personaBody(p: PersonaInput, actual?: Record<string, unknown>) {
  const emails = (actual?.emails ?? {}) as Record<string, unknown>;
  const phones = (actual?.phones ?? {}) as Record<string, unknown>;
  return {
    name: { firstName: p.nombre.trim(), lastName: p.apellido.trim() },
    jobTitle: p.cargo.trim(),
    emails: { ...emails, primaryEmail: p.email.trim().toLowerCase() },
    phones: { ...phones, ...phoneToField(p.telefono) },
    companyId: p.empresaId || null,
  };
}

export const crearPersona = async (p: PersonaInput) => toPersona(await create<RawPerson>('people', personaBody(p)));
export async function editarPersona(id: string, p: PersonaInput) {
  const actual = await get<Record<string, unknown>>('people', id, 0);
  return toPersona(await update<RawPerson>('people', id, personaBody(p, actual)));
}
export const borrarPersona = (id: string) => remove('people', id);
export const editarCampanasPersona = (id: string, noCampanas: boolean) => update('people', id, { noCampanas });

export async function buscarEmpresas(texto: string, limit = 60, after?: string) {
  const t = texto.trim();
  const page = await list<RawCompany>('companies', {
    filter: t ? f.or(f.ilike('name', t), f.ilike('address.addressCity', t), f.ilike('domainName.primaryLinkUrl', t)) : undefined,
    orderBy: 'name[AscNullsLast]',
    limit,
    after,
  });
  return { ...page, items: page.items.map(toEmpresa) };
}

export const todasLasEmpresas = async () => (await listAll<RawCompany>('companies', { orderBy: 'name[AscNullsLast]' }, 2000)).map(toEmpresa);
export const empresa = async (id: string) => toEmpresa(await get<RawCompany>('companies', id, 0));

export type EmpresaInput = { nombre: string; dominio: string; ciudad: string };
// Igual que personaBody: dominio y direccion se mezclan con lo que ya tiene la empresa (enlaces secundarios, calle,
// departamento, pais...) para no borrarlos al editar.
function empresaBody(e: EmpresaInput, actual?: Record<string, unknown>) {
  const dominio = (actual?.domainName ?? {}) as Record<string, unknown>;
  const direccion = (actual?.address ?? {}) as Record<string, unknown>;
  return {
    name: e.nombre.trim(),
    domainName: { ...dominio, primaryLinkUrl: e.dominio.trim() },
    address: { ...direccion, addressCity: e.ciudad.trim() },
  };
}
export const crearEmpresa = async (e: EmpresaInput) => toEmpresa(await create<RawCompany>('companies', empresaBody(e)));
export async function editarEmpresa(id: string, e: EmpresaInput) {
  const actual = await get<Record<string, unknown>>('companies', id, 0);
  return toEmpresa(await update<RawCompany>('companies', id, empresaBody(e, actual)));
}
export const borrarEmpresa = (id: string) => remove('companies', id);

/* ---------------- oportunidades ---------------- */

export async function oportunidades() {
  const items = await listAll<RawOpp>('opportunities', { orderBy: 'position[AscNullsLast]', depth: 1 }, 1000);
  return items.map(toOportunidad);
}

export async function oportunidadesDeEmpresa(empresaId: string) {
  const items = await listAll<RawOpp>('opportunities', { filter: f.eq('companyId', empresaId), depth: 1 }, 200);
  return items.map(toOportunidad);
}

export const moverOportunidad = (id: string, etapa: string, posicion?: number) =>
  update('opportunities', id, { stage: etapa, ...(posicion !== undefined ? { position: posicion } : {}) });

export type OportunidadInput = { nombre: string; monto: number; moneda: string; etapa: string; empresaId: string | null; contactoId: string | null; cierre: string | null; responsableId: string | null };
const oportunidadBody = (o: OportunidadInput) => ({
  name: o.nombre.trim(),
  amount: { amountMicros: toMicros(o.monto), currencyCode: o.moneda || 'COP' },
  stage: o.etapa,
  companyId: o.empresaId || null,
  pointOfContactId: o.contactoId || null,
  closeDate: o.cierre || null,
  ownerId: o.responsableId || null,
});
export const crearOportunidad = async (o: OportunidadInput) => toOportunidad(await create<RawOpp>('opportunities', oportunidadBody(o)));
export const editarOportunidad = async (id: string, o: OportunidadInput) => toOportunidad(await update<RawOpp>('opportunities', id, oportunidadBody(o)));
// Twenty borra "suave": el registro queda en su papelera y se puede restaurar desde Twenty.
export const borrarOportunidad = (id: string) => remove('opportunities', id);

export const oportunidad = async (id: string) => toOportunidad(await get<RawOpp>('opportunities', id, 1));

// Oportunidades donde la persona es el contacto (no todas las de su empresa).
export async function oportunidadesDePersona(personaId: string) {
  const items = await listAll<RawOpp>('opportunities', { filter: f.eq('pointOfContactId', personaId), depth: 1 }, 200);
  return items.map(toOportunidad);
}

export async function tareasDeOportunidad(oportunidadId: string) {
  const targets = await listAll<{ task: RawTask | null }>('taskTargets', { filter: f.eq('targetOpportunityId', oportunidadId), depth: 1 }, 200);
  return targets.filter((t) => t.task).map((t) => toTarea(t.task as RawTask));
}

// Etapas del pipeline desde la metadata de Twenty (respeta etapas personalizadas).
const ETAPA_ES: Record<string, string> = {
  NEW: 'Nuevo', SCREENING: 'Calificación', MEETING: 'Reunión', PROPOSAL: 'Propuesta', CUSTOMER: 'Ganado',
};

export async function etapas(): Promise<Etapa[]> {
  type Obj = { nameSingular: string; fields: { name: string; options?: { value: string; label: string; color?: string; position: number }[] }[] };
  const res = await request<{ data: Obj[] | { objects: Obj[] } }>('/rest/metadata/objects', { query: { limit: 200 } });
  const objetos = Array.isArray(res.data) ? res.data : res.data.objects;
  const opp = objetos.find((o) => o.nameSingular === 'opportunity');
  const campo = opp?.fields.find((x) => x.name === 'stage');
  const ops = campo?.options ?? Object.keys(ETAPA_ES).map((v, i) => ({ value: v, label: v, position: i }));
  return [...ops]
    .sort((a, b) => a.position - b.position)
    .map((o) => ({ valor: o.value, etiqueta: ETAPA_ES[o.value] ?? o.label, color: (o as { color?: string }).color, posicion: o.position }));
}

/* ---------------- llamadas ---------------- */

// Llamadas, tipificaciones y tarifas son objetos de la app Xmartek 3CX. En una empresa sin la app el motor responde
// 400 (el objeto no existe): se muestra vacio en lugar de un error; Ajustes > Licencia explica que falta la app.
async function deLaApp3cx<T>(consulta: Promise<T[]>): Promise<T[]> {
  try {
    return await consulta;
  } catch (e) {
    if (e instanceof ApiError && (e.status === 400 || e.status === 404)) return [];
    throw e;
  }
}

export async function llamadas({ desde, hasta, personaId, empresaId, agente, max = 500 }: { desde?: Date; hasta?: Date; personaId?: string; empresaId?: string; agente?: string; max?: number } = {}) {
  const items = await deLaApp3cx(
    listAll<RawLlamada>('llamadas', {
      filter: f.and(
        desde && f.gte('inicio', desde.toISOString()),
        hasta && f.lte('inicio', hasta.toISOString()),
        personaId && f.eq('personaId', personaId),
        empresaId && f.eq('empresaId', empresaId),
        agente && f.eq('agente', agente),
      ),
      orderBy: 'inicio[DescNullsLast]',
      depth: 1,
    }, max),
  );
  return items.map(toLlamada);
}

// Llamadas recientes (contadores del menu, Hoy, Llamadas, la isla): por GraphQL con solo los campos que se usan. La
// REST con depth=1 traia la persona, la empresa y la tipificacion completas en cada fila (hasta 1500 filas cada pocos
// segundos). Devuelve lo mismo que llamadas(). Si el motor rechaza la consulta (p. ej. un campo cambio de nombre) se
// usa la REST de siempre, para que los contadores nunca queden en cero por esto.
const CAMPOS_LLAMADA =
  'id name direccion estado numero agente inicio duracionSeg costo{ amountMicros currencyCode } destino gestion notas personaId empresaId tipificacionId createdAt persona{ id name{ firstName lastName } } empresa{ id name } tipificacion{ id name }';

export async function llamadasRecientes(desde: Date, max = 1500): Promise<Llamada[]> {
  try {
    const items = await gqlTodos<RawLlamada>('llamadas', CAMPOS_LLAMADA, { filtro: { inicio: { gte: desde.toISOString() } }, orden: [{ inicio: 'DescNullsLast' }], max });
    return items.map(toLlamada);
  } catch (e) {
    if (e instanceof ApiError && e.status === 400) return llamadas({ desde, max });
    throw e;
  }
}

export const llamada = async (id: string) => toLlamada(await get<RawLlamada>('llamadas', id, 1));

export async function tipificaciones() {
  const items = await deLaApp3cx(listAll<{ id: string; name: string; categoria: string | null; requiereSeguimiento: boolean }>('tipificaciones', { orderBy: 'position[AscNullsLast]' }, 200));
  return items.map<Tipificacion>((t) => ({ id: t.id, nombre: t.name, categoria: t.categoria, requiereSeguimiento: !!t.requiereSeguimiento }));
}

// Categorias de tipificacion de la app 3CX (campo SELECT del objeto tipificacion).
export const CATEGORIAS_TIPIFICACION: [string, string][] = [
  ['VENTA', 'Venta'],
  ['SOPORTE', 'Soporte'],
  ['RECLAMO', 'Reclamo'],
  ['INFORMACION', 'Información'],
  ['NO_EFECTIVA', 'No efectiva'],
];

type TipificacionInput = { nombre: string; categoria: string | null; requiereSeguimiento: boolean };
const tipificacionBody = (t: TipificacionInput) => ({ name: t.nombre.trim(), categoria: t.categoria || null, requiereSeguimiento: t.requiereSeguimiento });

export const crearTipificacion = (t: TipificacionInput) => create('tipificaciones', tipificacionBody(t));
export const editarTipificacion = (id: string, t: TipificacionInput) => update('tipificaciones', id, tipificacionBody(t));
// Borrado suave del motor: las llamadas ya tipificadas conservan su historia.
export const borrarTipificacion = (id: string) => remove('tipificaciones', id);

export async function tarifas() {
  type Raw = { id: string; name: string; prefijo: string; costoMinuto?: { amountMicros?: number; currencyCode?: string }; cargoConexion?: { amountMicros?: number }; incrementoSeg?: number };
  const items = await deLaApp3cx(listAll<Raw>('tarifas', { orderBy: 'position[AscNullsLast]' }, 500));
  return items.map<Tarifa>((t) => ({
    id: t.id,
    nombre: t.name,
    prefijo: t.prefijo ?? '',
    costoMinuto: fromMicros(t.costoMinuto?.amountMicros),
    cargoConexion: fromMicros(t.cargoConexion?.amountMicros),
    incrementoSeg: t.incrementoSeg ?? 60,
    moneda: t.costoMinuto?.currencyCode || 'COP',
  }));
}

export const editarTarifa = (id: string, t: { nombre: string; prefijo: string; costoMinuto: number; cargoConexion: number; incrementoSeg: number; moneda: string }) =>
  update('tarifas', id, {
    name: t.nombre.trim(),
    prefijo: t.prefijo.replace(/\D/g, ''),
    costoMinuto: { amountMicros: toMicros(t.costoMinuto), currencyCode: t.moneda },
    cargoConexion: { amountMicros: toMicros(t.cargoConexion), currencyCode: t.moneda },
    incrementoSeg: Math.max(1, Math.round(t.incrementoSeg)),
  });

export const crearTarifa = (t: { nombre: string; prefijo: string; costoMinuto: number; cargoConexion: number; incrementoSeg: number; moneda: string }) =>
  create('tarifas', {
    name: t.nombre.trim(),
    prefijo: t.prefijo.replace(/\D/g, ''),
    costoMinuto: { amountMicros: toMicros(t.costoMinuto), currencyCode: t.moneda },
    cargoConexion: { amountMicros: toMicros(t.cargoConexion), currencyCode: t.moneda },
    incrementoSeg: Math.max(1, Math.round(t.incrementoSeg)),
  });

export const borrarTarifa = (id: string) => remove('tarifas', id);

/* ---------------- tareas y notas ---------------- */

export async function tareasPendientes() {
  const items = await listAll<RawTask>('tasks', { filter: f.neq('status', 'DONE'), orderBy: 'dueAt[AscNullsLast]', depth: 1 }, 300);
  const tareas = items.map(toTarea);
  // La API REST solo expande un nivel: los nombres de las personas se traen aparte.
  const ids = [...new Set(tareas.map((t) => t.personaId).filter(Boolean) as string[])];
  if (ids.length) {
    const personas = await listAll<RawPerson>('people', { filter: f.in('id', ids), depth: 0 }, 300);
    const porId = new Map(personas.map((p) => [p.id, toPersona(p)]));
    for (const t of tareas) {
      const p = t.personaId ? porId.get(t.personaId) : undefined;
      if (p) t.persona = { id: p.id, nombre: p.nombreCompleto };
    }
  }
  return tareas;
}

export async function tareasDePersona(personaId: string) {
  const targets = await listAll<{ task: RawTask | null; targetPersonId: string }>('taskTargets', { filter: f.eq('targetPersonId', personaId), depth: 1 }, 200);
  return targets.filter((t) => t.task).map((t) => ({ ...toTarea(t.task as RawTask), personaId }));
}

export async function tareasDeEmpresa(empresaId: string) {
  const targets = await listAll<{ task: RawTask | null }>('taskTargets', { filter: f.eq('targetCompanyId', empresaId), depth: 1 }, 200);
  return targets.filter((t) => t.task).map((t) => toTarea(t.task as RawTask));
}

export const cambiarEstadoTarea = (id: string, estado: Tarea['estado']) => update('tasks', id, { status: estado });

// Una tarea completa para su ficha: con los nombres del contacto y la empresa a los que esta vinculada.
export async function tarea(id: string): Promise<Tarea> {
  const t = toTarea(await get<RawTask>('tasks', id, 1));
  const [p, e, o] = await Promise.all([
    t.personaId ? get<RawPerson>('people', t.personaId, 0).catch(() => null) : null,
    t.empresaId ? get<RawCompany>('companies', t.empresaId, 0).catch(() => null) : null,
    t.oportunidadId ? get<{ id: string; name: string }>('opportunities', t.oportunidadId, 0).catch(() => null) : null,
  ]);
  if (p) t.persona = { id: p.id, nombre: toPersona(p).nombreCompleto };
  if (e) t.empresa = { id: e.id, nombre: toEmpresa(e).nombre };
  if (o) t.oportunidad = { id: o.id, nombre: o.name || 'Oportunidad' };
  return t;
}

export type TareaCambios = { titulo: string; cuerpo: string; vence: string | null; estado: Tarea['estado']; asignadoId: string | null };
export const editarTarea = (id: string, c: TareaCambios) =>
  update('tasks', id, { title: c.titulo.trim(), bodyV2: { markdown: c.cuerpo, blocknote: null }, dueAt: c.vence, status: c.estado, assigneeId: c.asignadoId });

export const borrarTarea = (id: string) => remove('tasks', id);

// Hechas en los ultimos 30 dias (la lista de pendientes no las trae).
export async function tareasHechas() {
  const desde = new Date(Date.now() - 30 * 864e5).toISOString();
  const items = await listAll<RawTask>('tasks', { filter: f.and(f.eq('status', 'DONE'), f.gte('updatedAt', desde)), orderBy: 'updatedAt[DescNullsLast]', depth: 1 }, 300);
  return items.map(toTarea);
}

export async function crearTarea({ titulo, vence, asignadoId, personaId, empresaId, oportunidadId, cuerpo }: { titulo: string; vence: string | null; asignadoId?: string | null; personaId?: string | null; empresaId?: string | null; oportunidadId?: string | null; cuerpo?: string }) {
  const t = await create<RawTask>('tasks', {
    title: titulo.trim(),
    status: 'TODO',
    dueAt: vence,
    ...(asignadoId ? { assigneeId: asignadoId } : {}),
    ...(cuerpo ? { bodyV2: { markdown: cuerpo, blocknote: null } } : {}),
  });
  if (personaId || empresaId || oportunidadId) {
    await create('taskTargets', { taskId: t.id, targetPersonId: personaId || null, targetCompanyId: empresaId || null, targetOpportunityId: oportunidadId || null });
  }
  return toTarea(t);
}

// Notas de una persona, una empresa o una oportunidad (vinculadas por noteTargets).
export async function notasDe({ personaId, empresaId, oportunidadId }: { personaId?: string; empresaId?: string; oportunidadId?: string }) {
  type Raw = { note: { id: string; title: string; bodyV2?: { markdown?: string }; createdAt: string; createdBy?: { name?: string } } | null };
  const filter = personaId ? f.eq('targetPersonId', personaId) : oportunidadId ? f.eq('targetOpportunityId', oportunidadId) : f.eq('targetCompanyId', empresaId as string);
  const targets = await listAll<Raw>('noteTargets', { filter, depth: 1 }, 200);
  return targets
    .filter((t) => t.note)
    .map<Nota>((t) => ({
      id: t.note!.id,
      titulo: t.note!.title ?? '',
      cuerpo: t.note!.bodyV2?.markdown ?? '',
      creado: t.note!.createdAt,
      autor: t.note!.createdBy?.name,
    }))
    .sort((a, b) => b.creado.localeCompare(a.creado));
}

export async function crearNota({ texto, personaId, empresaId, oportunidadId }: { texto: string; personaId?: string | null; empresaId?: string | null; oportunidadId?: string | null }) {
  const limpio = texto.trim();
  const titulo = limpio.split('\n')[0].slice(0, 80);
  const n = await create<{ id: string; createdAt: string }>('notes', { title: titulo, bodyV2: { markdown: limpio, blocknote: null } });
  await create('noteTargets', { noteId: n.id, targetPersonId: personaId || null, targetCompanyId: empresaId || null, targetOpportunityId: oportunidadId || null });
  return { id: n.id, titulo, cuerpo: limpio, creado: n.createdAt } satisfies Nota;
}

export function editarNota(id: string, texto: string) {
  const limpio = texto.trim();
  return update('notes', id, { title: limpio.split('\n')[0].slice(0, 80), bodyV2: { markdown: limpio, blocknote: null } });
}

export const borrarNota = (id: string) => remove('notes', id);

/* ---------------- correos (si el workspace tiene correo conectado) ---------------- */

// Correos con un contacto, solo de los buzones que esta persona puede leer (servicio de correo, D38).
export async function correosDePersona(personaId: string): Promise<Correo[]> {
  try {
    return (await request<{ correos: Correo[] }>(`/correo/api/persona/${personaId}`)).correos;
  } catch {
    return [];
  }
}

/* ---------------- miembros ---------------- */

export async function miembros() {
  const items = await listAll<{ id: string; name: { firstName: string; lastName: string }; userEmail: string }>('workspaceMembers', {}, 500);
  return items.map<Miembro>((m) => ({ id: m.id, nombre: memberName(m) || m.userEmail, email: m.userEmail }));
}

/* ---------------- rutas de la app Xmartek 3CX (/s/3cx/...) ---------------- */

export const licencia = () => request<Licencia>('/s/3cx/licencia/estado');

// Mi perfil: el nombre con que me ven en el CRM (miembro de la empresa).
// Via propia del motor para que cada persona edite su perfil: la edicion general de miembros exige permiso de
// administrador y fallaba para los agentes.
export const editarMiNombre = (miembroId: string, nombre: string, apellido: string) =>
  metadataSesion<{ updateWorkspaceMemberSettings: boolean }>('mutation($i:UpdateWorkspaceMemberSettingsInput!){ updateWorkspaceMemberSettings(input:$i) }', {
    i: { workspaceMemberId: miembroId, update: { name: { firstName: nombre.trim(), lastName: apellido.trim() } } },
  });
// Pide la licencia al Portal de Xmartek ahora mismo (despues de un cambio de plan).
export const sincronizarLicencia = () => request<Licencia & { sincronizada: boolean; detalle: string }>('/s/3cx/licencia', { method: 'POST' });

export async function buscarPorNumero(numero: string): Promise<ContactoLookup | null> {
  if (searchKey(numero).length < 7) return null;
  try {
    const r = await request<{ contactos: ContactoLookup[] }>('/s/3cx/contacto', { query: { numero } });
    return r.contactos?.[0] ?? null;
  } catch {
    // Sin la app o sin licencia: busqueda directa por REST.
    const page = await list<RawPerson>('people', { filter: f.like('phones.primaryPhoneNumber', searchKey(numero)), limit: 1, depth: 1 });
    const p = page.items[0] ? toPersona(page.items[0]) : null;
    return p ? { id: p.id, nombre: p.nombre, apellido: p.apellido, empresa: p.empresa?.nombre } : null;
  }
}

export type ReporteLlamada = {
  numero: string;
  tipo: 'Inbound' | 'Outbound' | 'Missed' | 'Notanswered';
  agente: string;
  duracion: number;
  inicio: string;
  contactoId?: string;
};

export const registrarLlamada = (r: ReporteLlamada) =>
  request<{ id?: string; duplicada?: boolean }>('/s/3cx/llamada', { method: 'POST', body: r });

export const tipificar = (llamadaId: string, tipificacionId: string, notas: string) =>
  request<{ id: string; gestion: string; tipificacion: string; tareaId: string | null }>('/s/3cx/tipificar', {
    method: 'POST',
    body: { llamadaId, tipificacionId, notas },
  });

export const clicParaLlamar = (numero: string, extension: string) =>
  request<{ ok: boolean }>('/s/3cx/llamar', { method: 'POST', body: { numero, extension } });

export const numeroLegible = formatPhone;
